import React, { useEffect, useState } from 'react';
import { Tab, Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { 
  Users, Layers, Zap, TrendingUp, Rocket, BrainCircuit, Award, 
  Building2, X, Send
} from 'lucide-react';
import { CandidateProfile } from '../../Candidate/Types/CandidateType';
import { getJobTemplateByCompanyRecruiterId } from '../../JobPostTemplate/Services/JobPostTemplateService';
import { JobTemplateRequest } from '../../JobPostTemplate/Types/JobPostTemplateType';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { initialInvitation, Invitation } from '../../InvitatationSystem/Types/InvitationSystemType';
import { TextArea } from '../../Shared/Component/forms/TextArea';
import { DropdownSingle } from '../../Shared/Component/forms/DropdownSingle';
import { Option } from '../../Admin/Types/AdminType';
import { mapToOptions } from '../../Shared/Component/forms/dropdownHelpers';
import { createInvitation } from '../../InvitatationSystem/Services/InvitationService';
import CandidateProfileView from '../../Candidate/Components/CandidateProfileView';
import { storage } from '../../Shared/Utils/authStorage';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';

interface CandidateProps {
  candidate: CandidateProfile & {
    averageResponseTime?: string;
    invitationStatus?: string;
  };
}

interface ScoreMetricProps {
  label: string;
  value: string | number; 
  bgClass?: string;
  textClass?: string;
  tooltip: string;
}

// --- SUB-COMPONENT: THE LARGE DOSSIER CARD (Inside Modal) ---
const CandidateFullDossier = ({ candidate }: CandidateProps) => {
  return (
    <div className="max-w-4xl mx-auto p-2 md:p-4 font-sans text-slate-100">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative group overflow-hidden bg-slate-900 border border-slate-800/80 rounded-[2rem] md:rounded-[2.5rem] shadow-2xl shadow-slate-950/50 transition-all"
      >
        <div className="hidden md:block absolute -top-24 -left-24 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl" />
        
        <div className="relative flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-800/60">
          <div className="w-full p-6 md:p-8 flex flex-col">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-wider">// Fit</span>
                  <div className="h-1.5 w-20 md:w-24 bg-slate-950 rounded-full overflow-hidden border border-slate-800/40">
                    <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${Number(candidate.overallScore) * 10}%` }} />
                  </div>
                  <span className="text-xs font-mono font-bold text-cyan-400">{candidate.overallScore}/10</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {candidate.targetRole?.map((role) => (
                    <span key={role} className="px-2 py-0.5 bg-slate-950 text-slate-300 text-[9px] font-mono font-bold rounded-md border border-slate-800 uppercase tracking-tight">
                      Target: {role}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 rounded-full border border-amber-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                <span className="text-[9px] font-mono font-bold text-amber-400 uppercase tracking-wider">Premium Talent</span>
              </div>
            </div>

            <Tab.Group>
              <Tab.List className="flex p-1 space-x-1 bg-slate-950/80 rounded-xl md:rounded-2xl mb-6 overflow-x-auto no-scrollbar border border-slate-800/40">
                {['Strategic', 'Technical', 'Wins'].map((tab) => (
                  <Tab
                    key={tab}
                    className={({ selected }) =>
                      `flex-1 min-w-[80px] py-2 md:py-2.5 text-[10px] font-mono font-bold uppercase tracking-widest rounded-lg md:rounded-xl transition-all outline-none
                      ${selected ? 'bg-slate-800 text-cyan-400 border border-slate-700/60 shadow-inner' : 'text-slate-400 hover:text-slate-200'}`
                    }
                  >
                    {tab}
                  </Tab>
                ))}
              </Tab.List>

              <Tab.Panels className="flex-1">
                <Tab.Panel className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-slate-950 rounded-xl border border-slate-800 flex-shrink-0"><Users className="w-4 h-4 text-cyan-400" /></div>
                      <div>
                        <p className="text-[9px] font-mono font-bold text-slate-500 uppercase">// Team Size</p>
                        <p className="text-sm font-bold text-slate-200">{candidate.teamSizeLed} Members</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-slate-950 rounded-xl border border-slate-800 flex-shrink-0"><Rocket className="w-4 h-4 text-emerald-400" /></div>
                      <div>
                        <p className="text-[9px] font-mono font-bold text-slate-500 uppercase">// Product Scale</p>
                        <p className="text-sm font-bold text-slate-200 truncate max-w-[150px]">{candidate.productScale?.[0]}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-slate-950 rounded-xl border border-slate-800 flex-shrink-0"><Layers className="w-4 h-4 text-purple-400" /></div>
                      <div>
                        <p className="text-[9px] font-mono font-bold text-slate-500 uppercase">// Architecture</p>
                        <p className="text-sm font-bold text-slate-200 truncate max-w-[150px]">{candidate.architectureExperience?.[0]}</p>
                      </div>
                    </div>
                  </div>
                </Tab.Panel>

                <Tab.Panel>
                  <div className="mb-4">
                    <p className="text-[9px] font-mono font-bold text-slate-500 uppercase mb-2 tracking-widest">// Core Stack</p>
                    <div className="flex flex-wrap gap-1.5">
                      {candidate.coreExpertise?.map((skill) => (
                        <span key={skill} className="px-2.5 py-1 bg-slate-950 text-cyan-400 text-[10px] font-mono font-bold rounded-lg border border-slate-800">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </Tab.Panel>

                <Tab.Panel>
                  <div className="space-y-2.5">
                    {candidate.topAchievements?.slice(0, 3).map((achievement, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-950/60 border border-slate-800/80 rounded-xl">
                        <Award className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <span className="text-[11px] font-medium text-slate-300 leading-tight">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>

            <div className="mt-6">
              <p className="text-[9px] font-mono font-bold text-slate-500 uppercase mb-2">// Pedigree</p>
              <div className="flex items-center gap-3 overflow-x-auto pb-2 no-scrollbar">
                {candidate.notableCompanies?.map((company) => (
                  <div key={company} className="flex items-center gap-1.5 whitespace-nowrap bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-xl">
                    <Building2 className="w-3 h-3 text-slate-500" />
                    <span className="text-[10px] font-mono font-semibold text-slate-300">{company}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button className="order-2 sm:order-1 px-6 py-3.5 text-[10px] font-mono font-bold text-slate-400 bg-slate-950 rounded-2xl hover:bg-slate-800 border border-slate-800 transition-all uppercase tracking-widest">
                Dossier
              </button>
              <button className="order-1 sm:order-2 flex-1 flex items-center justify-center gap-2 px-6 py-3.5 text-[10px] font-mono font-bold text-slate-950 bg-cyan-400 hover:bg-cyan-300 rounded-2xl shadow-lg shadow-cyan-500/10 uppercase tracking-widest transition-all">
                <Zap className="w-4 h-4 fill-current" />
                Interview
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 md:px-8 py-3 bg-slate-950/60 border-t border-slate-800/60 flex items-center overflow-x-auto no-scrollbar gap-6">
          <span className="flex items-center gap-2 text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">
            <span className={`w-1.5 h-1.5 rounded-full ${candidate.availabilityStatus === 'Immediate' ? 'bg-emerald-500' : 'bg-amber-500'} animate-pulse`} />
            {candidate.availabilityStatus}
          </span>
          <span className="flex items-center gap-2 text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">
            <BrainCircuit className="w-3.5 h-3.5 text-cyan-400" />
            {candidate.decisionSpeed}
          </span>
          <span className="flex items-center gap-2 text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">
            <TrendingUp className="w-3.5 h-3.5 text-purple-400" />
            {candidate.totalYearsOfExperience}Y Exp
          </span>
        </div>
      </motion.div>
    </div>
  );
};

const ScoreMetric = ({ label, value, bgClass = 'bg-slate-900 border-slate-800/80', textClass = 'text-slate-300', tooltip }: ScoreMetricProps) => {
  return (
    <div className={`group/metric relative p-2 rounded-xl border ${bgClass} flex flex-col justify-center transition-all hover:border-slate-700 cursor-help`}>
      <p className="text-[9px] uppercase font-mono font-bold text-slate-500 tracking-wider mb-0.5">{label}</p>
      <p className={`text-xs font-mono font-bold ${textClass}`}>
        {value}
      </p>

      {/* Cyber-dark Floating Tooltip */}
      <div className="absolute bottom-[115%] left-1/2 -translate-x-1/2 mb-1 w-52 hidden group-hover/metric:block z-50 p-2.5 rounded-xl text-center text-[10px] font-mono font-medium shadow-2xl border border-slate-700 bg-slate-900 text-slate-300 pointer-events-none transition-all duration-150">
        {tooltip}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-700" />
        <div className="absolute top-[99%] left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
      </div>
    </div>
  );
};

// --- MAIN EXPORT: COMPACT LIST CARD ---
const CandidateCardForRecruiter = ({ candidate }: CandidateProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const initials = `${candidate.firstName?.charAt(0) || ''}${candidate.lastName?.charAt(0) || ''}`;

  const {
    values,
    handleInputChange,
    handleValueChange,
  } = useFormController<Invitation>(initialInvitation);

  const [jobTemplates, setJobTemplates] = useState<JobTemplateRequest[]>([]);
  const [, setLoading] = useState(true);
  const [, setError] = useState("");
  const [jobSummarySlug, setJobSummarySlug] = useState<Option<string>[]>([]);
  const [alert, setAlert] = useState<AlertProps | null>(null);
      
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getJobTemplateByCompanyRecruiterId(
          storage.getUserId() || ""
        );

        if (response?.data) {
          const dataArray = Array.isArray(response.data) ? response.data : [response.data];
          setJobTemplates(dataArray);

          const formatted = mapToOptions(
            dataArray,
            "jobSummarySlug",
            "jobSummarySlug"
          );
          setJobSummarySlug(formatted);
        }
      } catch (err) {
        setError("Failed to load candidates");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async () => {
    try {
      const selectedTemplate = jobTemplates.find(
        (jt) => jt.jobSummarySlug === values.jobSummarySlug
      );

      if (!selectedTemplate) {
        setAlert({
          variant: "error",
          title: "System Notification",
          message: "Please select an assigned job scope.", 
          autoCloseMs: 5000,
          onClose: () => setAlert(null), 
        });
        return;
      }

      const payload = {
        recruiterId: storage.getUserId() || "",
        candidateId: candidate.userId,
        jobTemplateId: selectedTemplate.id,
        invitationStatus: "PENDING",
        message: values.message || "",
      };

      const result = await createInvitation(payload);

      if (result?.success) {
        setAlert({
          variant: "success",
          title: "Execution Confirmed",
          message: "Invitation dispatch cycle successfully completed.", 
          autoCloseMs: 5000,
          onClose: () => setAlert(null), 
        });
        setIsInviteOpen(false);
      } else {
        throw new Error(result?.message || "Failed to send invitation");
      }
    } catch (error: any) {
      console.error("Error:", error);
      setAlert({
        variant: "error",
        title: "Transaction Failure",
        message: error.message || "Invitation cycle faulted.", 
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    }
  };

  return (
    <>
      <div className="w-full max-w-5xl mx-auto mb-3 px-2 md:px-4 font-sans text-slate-100">
        {alert && (
          <div className="mb-4">
            <Alert {...alert} onClose={() => setAlert(null)} />
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="group relative bg-slate-900 border border-slate-800/80 rounded-2xl hover:border-slate-700 hover:shadow-2xl hover:shadow-slate-950/40 transition-all p-3 md:p-4 flex flex-col gap-4"
        >
          {/* TOP ROW: Identity & Basic Status */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center w-full sm:w-auto gap-4">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-tr from-cyan-500 to-blue-600 p-[1px] flex-shrink-0">
                <div className="w-full h-full bg-slate-950 rounded-[11px] flex items-center justify-center text-base md:text-lg font-mono font-black text-cyan-400 uppercase">
                  {initials}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base md:text-lg font-bold text-slate-100 truncate tracking-tight">
                    {candidate.firstName} {candidate.lastName}
                  </h3>
                  {candidate.profileVerified && (
                    <span className="text-cyan-400" title="Verified Hex Identity">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293l-4 4a1 1 0 01-1.414 0l-2-2a1 1 0 111.414-1.414L9 10.586l3.293-3.293a1 1 0 011.414 1.414z" />
                      </svg>
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-slate-400 text-[10px] md:text-xs font-mono">
                  <span className="text-cyan-400 font-bold">{candidate.currentRole}</span>
                  {candidate.currentCompany && (
                    <>
                      <span className="text-slate-600">•</span>
                      <span>{candidate.currentCompany}</span>
                    </>
                  )}
                  <span className="text-slate-600">•</span>
                  <span>{candidate.totalYearsOfExperience}Y Exp</span>
                  <span className="text-slate-600">•</span>
                  <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-1.5 rounded">{candidate.availabilityStatus}</span>
                </div>
              </div>
            </div>

            {/* Actions Panel */}
            <div className="flex items-center justify-between w-full sm:w-auto sm:justify-end gap-2 md:gap-3 border-t sm:border-t-0 border-slate-800/60 pt-3 sm:pt-0">
              <ScoreMetric 
                label="Expectation" 
                value={candidate.expectedCompensation || 0} 
                bgClass={candidate.canViewContactDetails ? "bg-emerald-500/5 border-emerald-500/20" : "bg-slate-950 border-slate-800/80"} 
                textClass={candidate.canViewContactDetails ? "text-emerald-400 font-bold" : "text-slate-300 font-bold"} 
                tooltip="Expected Remuneration Parameters."
              />
              
              <button
                onClick={() => setIsInviteOpen(true)}
                className="flex-1 sm:flex-none px-4 py-2 text-[10px] font-mono font-bold uppercase tracking-widest bg-cyan-400 hover:bg-cyan-300 text-slate-950 rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <Send className="w-3 h-3 fill-current" />
                Invite
              </button>

              <button
                onClick={() => setIsOpen(true)}
                className="flex-1 sm:flex-none px-4 py-2 text-[10px] font-mono font-bold uppercase tracking-widest bg-slate-950 text-slate-300 rounded-xl hover:bg-slate-800 transition-all border border-slate-800"
              >
                Profile
              </button>
            </div>
          </div>

          {/* BOTTOM ROW: Rich Metric Blocks */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 pt-3 border-t border-slate-800/60">
            <ScoreMetric 
              label="Overall" 
              value={`${candidate.overallScore || 0}/100`} 
              bgClass="bg-slate-950 border-slate-800/60" 
              textClass="text-cyan-400" 
              tooltip="Aggregated pipeline matching profile evaluation across engineering layers."
            />
            <ScoreMetric 
              label="Leadership" 
              value={`${candidate.leadershipScore || 0}/100`} 
              bgClass="bg-slate-950 border-slate-800/60" 
              textClass="text-purple-400" 
              tooltip="Personnel coordination vector, process engineering, and product level hierarchy execution."
            />
            <ScoreMetric 
              label="Architecture" 
              value={`${candidate.architectureScore || 0}/100`} 
              bgClass="bg-slate-950 border-slate-800/60" 
              textClass="text-blue-400" 
              tooltip="High level structure validation matrix, resilience mapping, and scalability layout capability."
            />
            <ScoreMetric 
              label="Impact" 
              value={`${candidate.businessImpactScore || 0}/100`} 
              bgClass="bg-slate-950 border-slate-800/60" 
              textClass="text-emerald-400" 
              tooltip="Direct enterprise value conversion ratio and operational outcome records."
            />
            <ScoreMetric 
              label="Achievement" 
              value={`${candidate.achievementScore || 0}/100`} 
              bgClass="bg-slate-950 border-slate-800/60" 
              textClass="text-amber-400" 
              tooltip="Performance metric verifying relative tier rankings across delivered solutions."
            />
            <ScoreMetric 
              label="Avg Time" 
              value={candidate.averageResponseTime || "24 Hrs"} 
              bgClass="bg-slate-950 border-slate-800/60" 
              textClass="text-slate-400" 
              tooltip="Telemetry latency for communications response validation cycles."
            />
            <ScoreMetric 
              label="Notice Period" 
              value={
                candidate.noticePeriodDays === null || candidate.noticePeriodDays === undefined
                  ? "Not Listed"
                  : Number(candidate.noticePeriodDays) <= 15
                  ? "Immediate"
                  : `${candidate.noticePeriodDays} Days`
              }
              bgClass="bg-slate-950 border-slate-800/60" 
              textClass="text-slate-300" 
              tooltip="Pipeline clearance operational timeframe window requirements."
            />
          </div>
        </motion.div>
      </div>

      {/* Profile Modal */}
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform transition-all relative">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="absolute -top-2 -right-2 z-20 p-2 bg-slate-900 rounded-full shadow-2xl border border-slate-800 text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {candidate && (
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-4 overflow-hidden shadow-2xl">
                      <CandidateProfileView data={candidate} />
                    </div>
                  )}    
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Invite Modal */}
      <Transition show={isInviteOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsInviteOpen(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-slate-900 rounded-3xl p-6 w-full max-w-3xl shadow-2xl border border-slate-800/80 text-slate-100">
                <Dialog.Title className="text-lg font-bold tracking-tight text-slate-100 mb-4">
                Send Invitation to  {candidate.firstName}
                </Dialog.Title>
                
                <div className="space-y-4 mb-6">
                  <DropdownSingle
                    label="Select Job Template"
                    options={jobSummarySlug}
                    value={values.jobSummarySlug || ""}  
                    onChange={(val) => handleValueChange("jobSummarySlug")(val ?? "")} 
                  />

                  <TextArea 
                    label="Transmission Note "
                    name="message" 
                    value={values?.message} 
                    onChange={handleInputChange("message")} 
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setIsInviteOpen(false)}
                    className="flex-1 px-4 py-3 text-[10px] font-mono font-bold uppercase tracking-widest text-slate-400 bg-slate-950 border border-slate-800 rounded-xl hover:bg-slate-800 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 px-4 py-3 text-[10px] font-mono font-bold uppercase tracking-widest text-slate-950 bg-cyan-400 rounded-xl hover:bg-cyan-300 shadow-lg shadow-cyan-500/10 transition-all"
                  >
                    Send
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CandidateCardForRecruiter;